import { LucideIconName } from '@/components/custom-lucide/lucide-custom';
import { DossierFilterDirectories } from '@/constant/dictionaries';
import { useEmployeeInfoStore } from '@/hooks/use-employee-store';
import useMediaQuery from '@/hooks/use-media-query';
import { useSearchDebound } from '@/hooks/use-search-debound';
import {
    SidebarItemProps,
    sidebarRemoveWhenIsAdmin,
} from '@/hooks/use-sidebar-store';
import { AllSubKeysMessageResource } from '@/i18n/messages';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { MenuResponse } from '@/server/api/v1/menu/menu.routes';
import * as LucideIcons from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
    Dispatch,
    Key,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react';

export function convertToSidebarItem(
    items?: MenuResponse[],
    parentId?: string,
    menuPath?: string[],
    options?: {
        // Sử dụng ở trang quản lý
        isManagePage: boolean;

        /**Không có thông tin nhân viên */
        isNoEmpInfo: boolean;
    }
): SidebarItemProps[] {
    if (!items) {
        return [];
    }

    return items
        .map((x, idx) => {
            const _key = parentId ? `${parentId}_${x.ID}` : `${x.ID}`;
            let isParent = !!(x.IsParent && x.Menus?.length);
            let isHidden = x.IsHidden ?? false;
            let Icon;
            let iconClassName: string | undefined;
            let _children: SidebarItemProps[] = x.Menus
                ? convertToSidebarItem(x.Menus, _key, menuPath, options)
                : [];

            if (!options?.isManagePage) {
                /**Trường hợp là cha nhưng không có con --> ẩn */
                if (isParent && !x.Menus?.length) {
                    isHidden = true;
                }

                /**Trường hợp là node nhưng khong có path --> ẩn */
                if (!isParent && !x.Link) {
                    isHidden = true;
                }

                if (x.Link && x.Link.startsWith('/dossier/')) {
                    const splitMenu = x.Link.split('/');
                    iconClassName =
                        DossierFilterDirectories[
                            splitMenu[splitMenu.length - 1]
                        ]?.className;
                }
            }

            if (x.IconName) {
                Icon = LucideIcons[x.IconName as LucideIconName];
            }

            const result: SidebarItemProps = {
                _orginal: x,
                name: x.Name ?? '',
                href: x.Link,
                icon: Icon && {
                    component: Icon,
                    className: `w-5 h-5 ${iconClassName ? iconClassName : ''}`,
                },
                children: _children,
                id: x.ID,
                code: x.Code,
                description: x.Description,
                numberOfParent: x.NumberOfParent,
                key: _key,
                isHidden: isHidden, //? Luôn hiển thị ở trang quản lý
                isSystem: x.IsSystem,
                appLanguageResourceCode: x.AppLanguageResourceCode,
                appConfigurationIds: x.AppConfigurationIds,
            };

            if (menuPath && x.Link) {
                menuPath.push(x.Link);
            }

            return result;
        })
        .filter((x) => {
            // Nếu là trang quản lý ==> Hiện hết
            if (options?.isManagePage) return true;
            if (
                options?.isNoEmpInfo &&
                sidebarRemoveWhenIsAdmin[x.id as string]
            ) {
                // KHông có nhân viên ==> Ẩn menu công việc
                return false;
            }
            return !x.isHidden && (x.href || x.children?.length);
        });
}

/**
 * Tạo ra các data để sử dụng
 */
export function createSidebarDictionary(items: SidebarItemProps[]) {
    let obj: Record<string, (string | undefined)[]> = {};
    let arrSearch: SidebarItemProps[] = [];
    const _loop = (
        data: SidebarItemProps[],
        parentKeys?: (string | undefined)[]
    ) => {
        data.forEach((x) => {
            if (x.children && x.children.length > 0) {
                _loop(x.children, [...(parentKeys ?? []), x.key]);
            } else {
                if (x.href) {
                    arrSearch.push(x);
                    let href = obj[x.href];
                    if (!href) {
                        href = [...(parentKeys ?? []), x.key];
                    } else {
                        href = href.concat([...(parentKeys ?? []), x.key]);
                    }
                    obj[x.href] = [...new Set(href)];
                }
            }
        });
    };
    _loop(items);
    return {
        dictionary: obj,
        dataForSearch: arrSearch,
    };
}

/**
 * useSidebarHooks
 * @param pathname
 * @param items
 * @param menuForSearch
 * @param mediaQueryBreakdown
 * @param setMenuForSearch
 * @param setObjActive
 * @param setIsShowSidebarIconOnly
 * @param setMenus
 * @param setIsLoading
 * @param setSearchItems
 * @returns
 */
export const useSidebarState = (
    pathname: string,
    items: MenuResponse[] | undefined,
    menuForSearch: SidebarItemProps[],
    mediaQueryBreakdown: Key,
    setMenuForSearch: (value: SidebarItemProps[]) => void,
    setObjActive: (
        fn:
            | Record<string, boolean>
            | ((t: Record<string, boolean>) => Record<string, boolean>)
    ) => void,
    setIsShowSidebarIconOnly: (fn: boolean | ((t: boolean) => boolean)) => void,
    setMenus: (data: SidebarItemProps[]) => void,
    setIsLoading: (val: boolean) => void,
    setSearchItems?: Dispatch<SetStateAction<SidebarItemProps[]>>
) => {
    const transSidebar = useTranslations('Sidebar');

    const authData = useAuthenticationStore((x) => x.auth);
    const { employeeInfos, isInitedEmpInfo } = useEmployeeInfoStore((x) => x);

    const [hrefPairNoteKeys, setHrefPairNoteKeys] = useState<
        Record<string, (string | undefined)[]>
    >({});
    const [arrMenuPath, setArrMenuPath] = useState<string[]>([]);
    const [isInitial, setIsInitial] = useState(true);

    useEffect(() => {
        if (!authData || !isInitedEmpInfo) return;
        const menuPath: string[] = []; //Lấy ra danh sách các path menu hiện có user
        /**Loop qua diwx liệu menu lấy từ API */
        const _fillData = convertToSidebarItem(items, undefined, menuPath, {
            isManagePage: false,
            isNoEmpInfo: employeeInfos?.length === 0,
        });
        // map data api to mockup data
        const { dictionary, dataForSearch } =
            createSidebarDictionary(_fillData); // map data for searhc, dictionary

        setMenuForSearch(dataForSearch);
        setHrefPairNoteKeys(dictionary);
        setMenus(_fillData);
        setArrMenuPath(menuPath);
        setIsInitial(false);
    }, [
        authData,
        employeeInfos?.length,
        isInitedEmpInfo,
        items,
        setMenuForSearch,
        setMenus,
    ]);

    useEffect(() => {
        const _active: Record<string, boolean> = {};
        hrefPairNoteKeys[pathname]?.forEach((x, idx) => {
            const maxLength = (hrefPairNoteKeys[pathname]?.length ?? 0) - 1;
            /**
             * Nếu path có tồn tại và khác phần tử cuối. Vì phần tử cuối là leaf chứ không phải parent
             */
            if (x != undefined && idx !== maxLength) {
                _active[x] = true;
            }
        });

        setObjActive((st) => ({ ...st, ..._active }));
    }, [hrefPairNoteKeys, pathname, setObjActive]);

    const isDesktop = useMediaQuery(`(min-width: ${mediaQueryBreakdown}px)`);

    useEffect(() => {
        if (!isDesktop) {
            setIsShowSidebarIconOnly?.(false);
        }
    }, [isDesktop, setIsShowSidebarIconOnly]);

    const handleSearch = useCallback(
        (keyword: string) => {
            if (setSearchItems) {
                const rgx = new RegExp(keyword, 'gi');
                const finded = menuForSearch.filter((x) => {
                    let strName = x.name ?? '';

                    if (x.appLanguageResourceCode) {
                        strName = transSidebar(
                            x.appLanguageResourceCode as AllSubKeysMessageResource<'Sidebar'>
                        );
                    }

                    return strName.match(rgx) || x.href?.match(rgx);
                });
                setSearchItems(finded);
            }
        },
        [menuForSearch, setSearchItems, transSidebar]
    );
    const { keyword, setKeyword } = useSearchDebound(
        setIsLoading,
        handleSearch
    );

    return {
        /**Đang khởi tạo menu */
        isInitial,
        hrefPairNoteKeys,
        setHrefPairNoteKeys,
        isDesktop,
        keyword,
        setKeyword,
        arrMenuPath,
    };
};
